import type { ZodType } from 'zod'
import type { ProjectFormData } from '@/features/projects/utils/validation'
import {
  type AssistantToolCommercialPolicy,
  type AssistantToolKind,
  type AssistantToolTransport,
  getAssistantToolCatalogDefinition,
} from '@/features/assistant/tool-catalog'
import {
  createAssistantProject,
  getCurrentProject,
  openAssistantProject,
  analyzeCurrentProjectStructure,
  readCurrentProjectDiskSnapshot,
} from '@/features/assistant/tools/project-tools'
import {
  analyzeProjectMedia,
  importLocalMedia,
  listProjectMedia,
} from '@/features/assistant/tools/media-tools'
import { generateBackgroundMusic } from '@/features/assistant/tools/music-tools'
import { startAssistantExport } from '@/features/assistant/tools/export-tools'
import { getRuntimeCapabilities } from '@/features/assistant/tools/runtime-tools'
import {
  summarizeTimeline,
  insertAudioToTimeline,
  insertLoopingAudioToTimeline,
  createAssistantTrack,
  insertTextOverlay,
  insertImageOverlay,
  setItemTransform,
  addItemKeyframes,
  applyTransition,
  applyItemEffectPreset,
  createMaskForItem,
  trimClip,
  splitClip,
  setAudioVolume,
  setAudioFade,
  duplicateAssistantItem,
  rippleDeleteRange,
  setInOutPoints,
  updateCaptionItems,
  freezeFrameAt,
} from '@/features/assistant/tools/timeline-tools'
import {
  draftEditPlan,
  getAssistantPlanStatus,
  confirmDraftPlan,
} from '@/features/assistant/tools/planning-tools'
import { executeAssistantPlan } from '@/features/assistant/orchestrator'
import { createVoiceoverStepExecutors } from '@/features/assistant/executors/voiceover-executors'
import { parseAssistantPrompt } from '@/features/assistant/planner/prompt-parser'
import type { VoiceoverVideoAssistantContext } from '@/features/assistant/planner/voiceover-video-planner'
import {
  generateTtsAudio,
  saveGeneratedAudioToProjectMedia,
} from '@/features/assistant/tools/tts-tools'
import {
  insertTranscriptAsCaptions,
  transcribeMedia,
} from '@/features/assistant/tools/transcription-tools'
import {
  estimateTimelineCleanupSeconds,
  removeFillerWordsWithAssistant,
  removeSilenceWithAssistant,
} from '@/features/assistant/tools/cleanup-tools'
import { replaceCaptionStyle } from '@/features/assistant/tools/caption-style-tools'
import { addBrandWatermark } from '@/features/assistant/tools/watermark-tools'
import { estimateTtsProcessingSeconds } from '@/features/assistant/tools/fastcut-tool-access-client'
import type { AssistantToolContext } from '@/features/assistant/tool-context'
import { createAssistantToolContext } from '@/features/assistant/tool-context'
import {
  createInvalidToolInputError,
  normalizeAssistantToolError,
} from '@/features/assistant/tool-errors'
import { assertAssistantToolAccess } from '@/features/assistant/tool-access'
import {
  FastcutToolAccessError,
  commitToolAccess,
  releaseToolAccess,
  reserveToolAccess,
} from '@/features/assistant/tools/fastcut-tool-access-client'
import { getMedia } from '@/infrastructure/storage'
import { useTimelineStore } from '@/features/timeline/stores/timeline-store'
import type { AssistantPlan, AssistantStep } from '@/features/assistant/types'

export interface AssistantToolDefinition<TInput = unknown, TOutput = unknown> {
  name: string
  description: string
  kind: AssistantToolKind
  transport: AssistantToolTransport
  preconditions?: string[]
  sideEffects?: string[]
  supportsProgress?: boolean
  supportsCancellation?: boolean
  commercialPolicy?: AssistantToolCommercialPolicy
  inputSchema: ZodType<TInput>
  outputSchema: ZodType<TOutput>
  execute: (input: TInput, context: AssistantToolContext) => Promise<unknown>
}

export type AssistantToolAnyDefinition = AssistantToolDefinition<any, any>
export type AssistantToolMap = Record<string, AssistantToolAnyDefinition>

const MCP_TIMELINE_PERSIST_SIDE_EFFECTS = new Set([
  'mutates-timeline',
  'creates-track',
  'creates-track-when-needed',
  'adds-keyframes',
  'adds-transition',
  'adds-effects',
])

function normalizeRawInput<TInput>(schema: ZodType<TInput>, input: unknown): TInput {
  const normalizedInput = input ?? {}
  const result = schema.safeParse(normalizedInput)
  if (!result.success) {
    throw createInvalidToolInputError(result.error)
  }
  return result.data
}

function defineAssistantTool<TInput, TOutput>(
  definition: AssistantToolDefinition<TInput, TOutput>,
): AssistantToolAnyDefinition {
  return {
    ...definition,
    async execute(input, context) {
      const normalizedInput = normalizeRawInput(definition.inputSchema, input)
      const policy = definition.commercialPolicy

      // For billable tools with a quota metric, do a real-time server-side
      // reservation before executing. This prevents concurrent over-consumption
      // that the local cache check cannot detect.
      let reservation:
        | {
            quotaMetric: string
            reservedUnits: number
            toolName: string
          }
        | null = null
      const requestedUnits = await estimateRequestedToolUnits(definition.name, normalizedInput)

      if (policy?.billable && policy.quotaMetric && context.accessSummary) {
        try {
          const result = await reserveToolAccess({
            toolName: definition.name,
            requestedUnits,
          })
          if (result.allowed && result.quotaMetric && result.reservedUnits > 0) {
            reservation = {
              quotaMetric: result.quotaMetric,
              reservedUnits: result.reservedUnits,
              toolName: result.toolName,
            }
          }
        } catch (error) {
          if (
            error instanceof FastcutToolAccessError &&
            error.status >= 400 &&
            error.status < 500
          ) {
            throw normalizeAssistantToolError(error)
          }

          // If the server reservation is temporarily unavailable, fall through
          // to the local access check as a best-effort fallback.
        }
      }

      try {
        assertAssistantToolAccess(definition, context)
        const output = await definition.execute(normalizedInput, context)
        const parsedOutput = definition.outputSchema.parse(output)

        if (shouldPersistTimelineForMcp(definition, context)) {
          await context.ensureWorkspaceReady()
          await useTimelineStore
            .getState()
            .saveTimeline(context.currentProjectId, { skipThumbnailGeneration: true })
        }

        // Commit the reservation on success
        if (reservation) {
          commitToolAccess(reservation).catch((commitError) => {
            console.error(
              `FastCut quota commit failed for ${definition.name}:`,
              commitError,
            )
          })
        }

        return parsedOutput
      } catch (error) {
        // Release the reservation on any failure
        if (reservation) {
          releaseToolAccess(reservation).catch((releaseError) => {
            console.error(
              `FastCut quota release failed for ${definition.name}:`,
              releaseError,
            )
          })
        }

        throw normalizeAssistantToolError(error)
      }
    },
  }
}

function shouldPersistTimelineForMcp(
  definition: AssistantToolDefinition<unknown, unknown>,
  context: AssistantToolContext,
): context is AssistantToolContext & { currentProjectId: string } {
  return (
    context.invocationSource === 'mcp' &&
    typeof context.currentProjectId === 'string' &&
    context.currentProjectId.length > 0 &&
    definition.sideEffects?.some((effect) => MCP_TIMELINE_PERSIST_SIDE_EFFECTS.has(effect)) === true
  )
}

async function estimateRequestedToolUnits(
  toolName: string,
  input: unknown,
): Promise<number | undefined> {
  if (!input || typeof input !== 'object') {
    return undefined
  }

  if (toolName === 'generate_tts_audio') {
    const text = 'text' in input && typeof input.text === 'string' ? input.text : ''
    return estimateTtsProcessingSeconds(text)
  }

  if (toolName === 'transcribe_media') {
    const mediaId = 'mediaId' in input && typeof input.mediaId === 'string' ? input.mediaId : ''
    if (!mediaId) {
      return undefined
    }

    const media = await getMedia(mediaId)
    if (!media) {
      return undefined
    }

    return Math.max(1, Math.ceil(media.duration || 0))
  }

  if (toolName === 'draft_edit_plan') {
    const selectedReferenceMediaIds =
      'selectedReferenceMediaIds' in input && Array.isArray(input.selectedReferenceMediaIds)
        ? input.selectedReferenceMediaIds.filter(
            (mediaId): mediaId is string => typeof mediaId === 'string' && mediaId.trim().length > 0,
          )
        : []

    if (selectedReferenceMediaIds.length > 0) {
      const referencedMedia = await Promise.all(
        selectedReferenceMediaIds.map((mediaId) => getMedia(mediaId)),
      )
      const totalDurationSeconds = referencedMedia.reduce(
        (sum, media) => sum + Math.max(0, Number(media?.duration ?? 0)),
        0,
      )

      if (totalDurationSeconds > 0) {
        return Math.max(1, Math.ceil(totalDurationSeconds))
      }
    }

    const prompt = 'prompt' in input && typeof input.prompt === 'string' ? input.prompt : ''
    if (prompt.trim()) {
      return Math.max(1, Math.ceil(parseAssistantPrompt(prompt).durationSeconds))
    }

    return 1
  }

  if (toolName === 'remove_filler_words') {
    return estimateTimelineCleanupSeconds(
      input as { itemIds?: readonly string[]; transcriptMediaId?: string },
    )
  }

  if (toolName === 'remove_silence') {
    return estimateTimelineCleanupSeconds(
      input as { itemIds?: readonly string[]; transcriptMediaId?: string },
    )
  }

  return undefined
}

function normalizePlanStepForExecution(step: AssistantStep): AssistantStep {
  return {
    ...step,
    status: 'pending',
    errorMessage: undefined,
    progressPercent: undefined,
    progressLabel: null,
    payload: step.payload ? { ...step.payload } : undefined,
  }
}

function selectPlanStep(
  plan: AssistantPlan<Record<string, unknown>>,
  input: { stepId?: string; stepIndex?: number },
): AssistantStep {
  if (typeof input.stepIndex === 'number') {
    const step = plan.steps[input.stepIndex]
    if (!step) {
      throw new Error(`Plan step index ${input.stepIndex} is out of range.`)
    }
    return step
  }

  const step = plan.steps.find((candidate) => candidate.id === input.stepId)
  if (!step) {
    throw new Error(`Plan step ${input.stepId} was not found.`)
  }
  return step
}

const assistantToolDefinitions: AssistantToolAnyDefinition[] = [
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('get_current_project')!,
    async execute(_input, context) {
      await context.ensureWorkspaceReady()
      return getCurrentProject()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('read_current_project_disk_snapshot')!,
    async execute(_input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return readCurrentProjectDiskSnapshot()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('analyze_current_project_structure')!,
    async execute(_input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return analyzeCurrentProjectStructure()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('create_project')!,
    async execute(input) {
      return createAssistantProject(input as ProjectFormData)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('open_project')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      return openAssistantProject(input.projectId)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('list_project_media')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      return listProjectMedia(input.projectId, { refresh: input.refresh })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('analyze_project_media')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return analyzeProjectMedia(input.projectId)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('import_local_media')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return importLocalMedia(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('summarize_timeline')!,
    async execute(_input, context) {
      context.ensureCurrentProject()
      return summarizeTimeline()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('draft_edit_plan')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      const plan = await draftEditPlan(input, context.samplingClient)
      const project = context.getCurrentProjectSnapshot()

      return project
        ? {
            ...plan,
            context: {
              ...plan.context,
              projectId: project.projectId,
              mediaProjectId: project.mediaProjectId,
              projectName: project.name,
              width: project.width,
              height: project.height,
              fps: project.fps,
            },
          }
        : plan
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('execute_plan_step')!,
    async execute(input) {
      const plan = input.plan as AssistantPlan<Record<string, unknown>>
      if (plan.taskType !== 'voiceover_video') {
        throw new Error(`Unsupported plan task type: ${plan.taskType}`)
      }

      const selectedStep = selectPlanStep(plan, {
        stepId: input.stepId,
        stepIndex: input.stepIndex,
      })
      const executionPlan: AssistantPlan<VoiceoverVideoAssistantContext> = {
        ...plan,
        context: {
          ...(plan.context as VoiceoverVideoAssistantContext),
          ...(input.contextOverride ?? {}),
        },
        steps: [normalizePlanStepForExecution(selectedStep)],
      }

      const job = await executeAssistantPlan(executionPlan, {
        executors: createVoiceoverStepExecutors(),
      })

      return {
        job,
        executedStep: job.steps[0],
      }
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('generate_tts_audio')!,
    async execute(input) {
      return generateTtsAudio(input as Parameters<typeof generateTtsAudio>[0])
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('save_generated_audio_to_media')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      return saveGeneratedAudioToProjectMedia(input.file, input.projectId, {
        tags: input.tags,
        codec: input.codec,
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('generate_bgm')!,
    async execute(input) {
      return generateBackgroundMusic({
        topic: input.topic,
        tone: input.tone,
        durationSeconds: input.durationSeconds,
        model: input.model as Parameters<typeof generateBackgroundMusic>[0]['model'],
        signal: input.signal,
        onProgress: input.onProgress as Parameters<typeof generateBackgroundMusic>[0]['onProgress'],
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('insert_audio_to_timeline')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return insertAudioToTimeline(
        input.media as Parameters<typeof insertAudioToTimeline>[0],
        input.blobUrl,
        {
          volumeDb: input.volumeDb,
        },
      )
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('attach_bgm_to_timeline')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return insertLoopingAudioToTimeline(
        input.media as Parameters<typeof insertLoopingAudioToTimeline>[0],
        input.blobUrl,
        {
          totalDurationSeconds: input.totalDurationSeconds,
          volumeDb: input.volumeDb,
          placement: input.placement,
        },
      )
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('transcribe_media')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return transcribeMedia(input.mediaId, {
        language: input.language,
        signal: input.signal,
        onProgress: input.onProgress,
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('insert_transcript_as_captions')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return insertTranscriptAsCaptions(input.mediaId, {
        clipIds: input.clipIds,
        replaceExisting: input.replaceExisting,
        preferredTrackAboveTrackId: input.preferredTrackAboveTrackId,
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('remove_filler_words')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return removeFillerWordsWithAssistant(input, {
        allowSelectionFallback: context.invocationSource !== 'mcp',
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('remove_silence')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return removeSilenceWithAssistant(input, {
        allowSelectionFallback: context.invocationSource !== 'mcp',
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('replace_caption_style')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return replaceCaptionStyle(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('add_brand_watermark')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return addBrandWatermark(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('create_track')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return createAssistantTrack(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('insert_text_overlay')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return insertTextOverlay(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('insert_image_overlay')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return insertImageOverlay(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('set_item_transform')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return setItemTransform(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('add_item_keyframes')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return addItemKeyframes(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('apply_transition')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return applyTransition(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('apply_item_effect_preset')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return applyItemEffectPreset(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('create_mask_for_item')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return createMaskForItem(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('trim_clip')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return trimClip(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('split_clip')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return splitClip(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('set_audio_volume')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return setAudioVolume(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('set_audio_fade')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return setAudioFade(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('duplicate_item')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return duplicateAssistantItem(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('ripple_delete_range')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return rippleDeleteRange(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('freeze_frame_at')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return freezeFrameAt(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('set_in_out_points')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return setInOutPoints(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('update_caption_items')!,
    async execute(input, context) {
      context.ensureCurrentProject()
      return updateCaptionItems(input)
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('get_plan_status')!,
    async execute() {
      return getAssistantPlanStatus()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('confirm_plan')!,
    async execute() {
      return confirmDraftPlan()
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('start_export')!,
    async execute(input, context) {
      await context.ensureWorkspaceReady()
      context.ensureCurrentProject()
      return startAssistantExport({
        settings: input.settings as NonNullable<
          Parameters<typeof startAssistantExport>[0]
        >['settings'],
        signal: input.signal,
        download: input.download,
        fileName: input.fileName,
        onProgress: input.onProgress as NonNullable<
          Parameters<typeof startAssistantExport>[0]
        >['onProgress'],
      })
    },
  }),
  defineAssistantTool({
    ...getAssistantToolCatalogDefinition('get_runtime_capabilities')!,
    async execute() {
      return getRuntimeCapabilities()
    },
  }),
]

const assistantToolDefinitionMap = assistantToolDefinitions.reduce<AssistantToolMap>(
  (map, definition) => {
    if (map[definition.name]) {
      throw new Error(`Duplicate assistant tool definition: ${definition.name}`)
    }

    map[definition.name] = definition
    return map
  },
  {},
)

export function getAssistantToolDefinitions(): AssistantToolAnyDefinition[] {
  return [...assistantToolDefinitions]
}

export function getAssistantToolDefinition(name: string): AssistantToolAnyDefinition | null {
  return assistantToolDefinitionMap[name] ?? null
}

export function getAssistantToolMap(): AssistantToolMap {
  return { ...assistantToolDefinitionMap }
}

export async function executeAssistantTool(
  name: string,
  input?: unknown,
  context = createAssistantToolContext(),
) {
  const definition = getAssistantToolDefinition(name)
  if (!definition) {
    throw new Error(`Unknown assistant tool: ${name}`)
  }

  return definition.execute(input as never, context)
}
